
import React, { useState, useRef, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FileText, Check, Loader2, AlertCircle, Lock } from "lucide-react";

type NotesVariant = 'public' | 'internal';

interface EstimateNotesFieldProps {
  note: string | undefined;
  onSave: (text: string) => Promise<void> | void;
  disabled?: boolean;
  /** 'public' (default) = note del preventivo; 'internal' = note private del team. */
  variant?: NotesVariant;
}

const MAX_LENGTH = 5000;
const DEBOUNCE_DELAY = 4000; // 4 secondi

const VARIANT_META: Record<NotesVariant, {
  label: string;
  hint: string;
  placeholder: string;
  icon: React.ReactNode;
  emptyLabel: string;
}> = {
  public: {
    label: 'Note preventivo',
    hint: 'Verranno stampate nel PDF inviato al cliente',
    placeholder: 'Annota informazioni utili sul preventivo: dettagli tecnici, richieste del cliente, esclusioni, condizioni particolari…',
    icon: <FileText className="w-4 h-4" />,
    emptyLabel: '+ Aggiungi note al preventivo',
  },
  internal: {
    label: 'Note interne',
    hint: 'Solo team — NON verranno stampate sul preventivo',
    placeholder: 'Margini, strategia commerciale, retroscena cliente, promemoria privati…',
    icon: <Lock className="w-4 h-4 text-amber-600" />,
    emptyLabel: '+ Aggiungi note interne (solo team)',
  },
};

const EstimateNotesField: React.FC<EstimateNotesFieldProps> = ({
  note,
  onSave,
  disabled,
  variant = 'public',
}) => {
  const meta = VARIANT_META[variant];
  const isInternal = variant === 'internal';
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note ?? "");
  const [charCount, setCharCount] = useState((note ?? "").length);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dirty, setDirty] = useState(false);
  const debounceTimeout = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // --- Effetto per gestire la debounced auto-save ---
  useEffect(() => {
    // Non eseguire autosave se non siamo in editing o il valore non è cambiato
    if (!editing || value === note) return;

    setDirty(true);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // Salva in automatico dopo un periodo di inattività
    debounceTimeout.current = window.setTimeout(async () => {
      await doSave();
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
    // eslint-disable-next-line
  }, [value]);

  // Effetto per aggiornare value quando arrivano note nuove da sopra
  useEffect(() => {
    setValue(note ?? "");
    setCharCount((note ?? "").length);
    setDirty(false);
    setEditing(false);
    // eslint-disable-next-line
  }, [note]);

  // Effetto per il resize automatico
  useEffect(() => {
    setCharCount(value.length);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [value, expanded]);

  // Funzione di salvataggio centrale
  const doSave = async () => {
    if (saving || value === note) {
      setDirty(false);
      return;
    }
    setSaving(true);
    await onSave(value);
    setSaving(false);
    setSuccess(true);
    setDirty(false);
    setTimeout(() => setSuccess(false), 1200);
  };

  // Save forzato da blur/input/button
  const handleForceSave = async () => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    await doSave();
  };

  const handleExpand = () => setExpanded(true);

  const handleStartEdit = () => {
    setEditing(true);
    setExpanded(true);
    setTimeout(() => textareaRef.current?.focus(), 10);
  };

  const handleCancel = () => {
    setValue(note ?? "");
    setEditing(false);
    setDirty(false);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
  };

  // Shortcut: Ctrl+Enter salva, Esc annulla
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.key === "Enter" && (e.ctrlKey || e.metaKey))) {
      e.preventDefault();
      handleForceSave();
      setEditing(false);
    }
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  // --- GRAFICA COMPATTA & MIGLIORAMENTI STYLE ---
  return (
    <div className="group w-full transition-all">
      <div className="flex items-center gap-2 text-gray-700 mb-0.5">
        {meta.icon}
        <span className="font-semibold text-sm">{meta.label}</span>
        {isInternal && (
          <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
            Solo team
          </span>
        )}
        {charCount > 0 && (
          <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 rounded">
            {charCount}/{MAX_LENGTH}
          </span>
        )}
        {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground ml-2" />}
        {dirty && !saving && (
          <span className="flex items-center text-[11px] text-yellow-600 ml-2">
            <AlertCircle className="w-3 h-3 mr-1" /> Modifiche non salvate
          </span>
        )}
        {success && <Check className="text-green-500 w-4 h-4 ml-2" />}
      </div>
      <p className={`text-[11px] mb-1.5 ${isInternal ? 'text-amber-700' : 'text-muted-foreground'}`}>
        {meta.hint}
      </p>
      {expanded ? (
        <div className="relative">
          <Textarea
            ref={textareaRef}
            className={`mt-1 w-full min-h-[140px] max-h-72 border resize-none text-sm shadow-sm ${isInternal ? 'bg-amber-50/30 border-amber-200' : 'bg-white'}`}
            placeholder={meta.placeholder}
            value={value}
            disabled={disabled || saving}
            maxLength={MAX_LENGTH}
            onChange={e => {
              setValue(e.target.value);
              setDirty(true);
              setEditing(true);
            }}
            onFocus={() => setEditing(true)}
            onBlur={handleForceSave}
            onKeyDown={handleKeyDown}
            autoFocus={editing}
          />
          <div className="mt-1 flex gap-2 justify-end">
            {editing && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    handleForceSave();
                    setEditing(false);
                  }}
                  disabled={saving || value === note}
                >
                  Salva
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Annulla
                </Button>
              </>
            )}
            {!editing && (
              <Button type="button" size="sm" variant="ghost" onClick={() => setExpanded(false)}>
                Chiudi
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div
          className={
            "w-full cursor-pointer rounded px-3 py-3 hover:bg-opacity-80 border min-h-[100px] text-sm transition-all shadow-sm flex " +
            (isInternal ? 'bg-amber-50/40 border-amber-200 hover:bg-amber-100/40 ' : 'bg-gray-50 border-gray-200 hover:bg-gray-100 ') +
            (note && note.trim() ? "text-gray-800 items-start" : "text-gray-400 items-center")
          }
          onClick={handleExpand}
        >
          {note && note.trim() ? (
            <div className="w-full flex justify-between items-start gap-2">
              <span className="whitespace-pre-wrap line-clamp-4 flex-1">
                {note}
              </span>
              <span className="ml-2 text-primary underline text-xs whitespace-nowrap shrink-0">Espandi</span>
            </div>
          ) : (
            <span>{meta.emptyLabel}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default EstimateNotesField;
